import { inject } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs';

import {
    ConfigureBlueSageFranchiseGQL,
    ConfigureBlueSageLocationGQL,
    GetBlueSageGQL,
    SetBlueSageEnabledGQL,
    SetBlueSageHoldsEnabledGQL,
} from 'src/generated/graphql.generated';

import { BlueSageSettingsActions } from './bluesage-settings.actions';
import { blueSageSettingsFeature } from './bluesage-settings.feature';

export const loadBlueSageSetup = createEffect((
    actions$ = inject(Actions),
    getBlueSageGQL = inject(GetBlueSageGQL),
) => {
    return actions$.pipe(
        ofType(
            BlueSageSettingsActions.panelOpened,
            BlueSageSettingsActions.refreshButtonClicked,
        ),
        // Requirement: K3/K4 - switchMap, never exhaustMap. `panelOpened` and
        // `refreshButtonClicked` both set `loading` in the reducer, so a trigger
        // dropped mid-flight leaves the panel spinning with no success or error
        // to follow. Same defect the review queue's read effects carried.
        switchMap((action) => of(action).pipe(
            // no-cache: this panel exists to answer "what is live right now", and a
            // cached answer after a save is worse than no answer.
            mergeMap(() => getBlueSageGQL.fetch({}, { fetchPolicy: 'no-cache' }).pipe(
                map((res) => BlueSageSettingsActions.loadSetupSuccess({ status: res.data.blueSage })),
            )),
            catchError((error) => of(BlueSageSettingsActions.loadSetupError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

export const saveBlueSageCredentials = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    configureFranchiseGQL = inject(ConfigureBlueSageFranchiseGQL),
) => {
    return actions$.pipe(
        ofType(BlueSageSettingsActions.saveCredentialsButtonClicked),
        exhaustMap((action) => of(action).pipe(
            concatLatestFrom(() => [
                store.select(blueSageSettingsFeature.selectApiKey),
                store.select(blueSageSettingsFeature.selectWebhookSigningSecret),
            ]),
            mergeMap(([ _action, apiKey, webhookSigningSecret ]) => configureFranchiseGQL.mutate({
                // Send only what was typed. An empty string is indistinguishable
                // from "clear this secret", and clearing a signing secret 401s
                // every inbound event until somebody notices payments stopped.
                ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
                ...(webhookSigningSecret.trim()
                    ? { webhookSigningSecret: webhookSigningSecret.trim() }
                    : {}),
            }).pipe(
                map((res) => BlueSageSettingsActions.saveCredentialsSuccess({
                    status: res.data.configureBlueSageFranchise,
                })),
            )),
            catchError((error) => of(BlueSageSettingsActions.saveCredentialsError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

export const saveBlueSageLocation = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    configureLocationGQL = inject(ConfigureBlueSageLocationGQL),
) => {
    return actions$.pipe(
        ofType(BlueSageSettingsActions.saveLocationButtonClicked),
        exhaustMap((action) => of(action).pipe(
            concatLatestFrom(() => store.select(blueSageSettingsFeature.selectLocationId)),
            mergeMap(([ _action, locationId ]) => configureLocationGQL.mutate({
                locationId: locationId.trim(),
            }).pipe(
                map((res) => BlueSageSettingsActions.saveLocationSuccess({
                    status: res.data.configureBlueSageLocation,
                })),
            )),
            // The server rejects anything that is not a bsp_loc_ id and says so in
            // plain language — worth surfacing verbatim, because a wrong location
            // id breaks inbound events silently rather than loudly.
            catchError((error) => of(BlueSageSettingsActions.saveLocationError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

export const setBlueSageEnabled = createEffect((
    actions$ = inject(Actions),
    setEnabledGQL = inject(SetBlueSageEnabledGQL),
) => {
    return actions$.pipe(
        ofType(BlueSageSettingsActions.paymentTypeSwitchToggled),
        exhaustMap((action) => of(action).pipe(
            mergeMap(({ enabled }) => setEnabledGQL.mutate({ enabled }).pipe(
                map((res) => BlueSageSettingsActions.setEnabledSuccess({
                    status: res.data.setBlueSageEnabled,
                })),
            )),
            catchError((error) => of(BlueSageSettingsActions.setEnabledError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

export const setBlueSageHoldsEnabled = createEffect((
    actions$ = inject(Actions),
    setHoldsEnabledGQL = inject(SetBlueSageHoldsEnabledGQL),
) => {
    return actions$.pipe(
        ofType(BlueSageSettingsActions.holdsSwitchToggled),
        exhaustMap((action) => of(action).pipe(
            mergeMap(({ enabled }) => setHoldsEnabledGQL.mutate({ enabled }).pipe(
                map((res) => BlueSageSettingsActions.setHoldsEnabledSuccess({
                    status: res.data.setBlueSageHoldsEnabled,
                })),
            )),
            catchError((error) => of(BlueSageSettingsActions.setHoldsEnabledError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

/**
 * A failed toggle leaves the switch showing something the server did not accept,
 * so re-read rather than guessing. Kept separate from the mutation effect above,
 * per our NgRx conventions: that one calls the mutation, this one reacts to
 * its outcome.
 */
export const refreshAfterFailedToggle = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(
            BlueSageSettingsActions.setEnabledError,
            BlueSageSettingsActions.setHoldsEnabledError,
        ),
        map(() => BlueSageSettingsActions.refreshButtonClicked()),
    );
}, { functional: true, dispatch: true });
